/**
 * Phase 3 friction suggestions — client helpers / vocabulary.
 */

export const FRICTION_SUGGESTION_TYPES = Object.freeze([
  'boot_prompt_direction',
  'ticket_instruction_norm',
  'platform_bug_flag',
]);

export const FRICTION_ANALYZE_RANGE_OPTIONS = Object.freeze([30, 60, 90]);

/**
 * @param {number} days
 * @param {Date=} end
 * @returns {{ start: string, end: string }}
 */
export function frictionPresetToDateRange(days, end = new Date()) {
  const endDate = end instanceof Date && !Number.isNaN(end.getTime()) ?
    end :
    new Date();
  const startDate = new Date(endDate.getTime() - days * 24 * 60 * 60 * 1000);
  return {
    start: toDateInputValue(startDate),
    end: toDateInputValue(endDate),
  };
}

/**
 * @param {Date} date
 * @returns {string} YYYY-MM-DD
 */
export function toDateInputValue(date) {
  const d = date instanceof Date ? date : new Date();
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Mirror of functions/lib/ticketBriefingConstants FRICTION_TAGS. */
export const FRICTION_TAGS_FOR_ANALYZE = Object.freeze([
  'patch_content_omission_loop',
  'patch_fragmentation',
  'spend_cap_hit',
  'rejection_strikeout_hit',
  'wrong_repo_context',
  'hallucinated_narration',
  'cross_repo_blocker',
  'tool_call_error',
  'judge_rejection',
  'human_correction_required',
  'other',
]);

/**
 * Prefill marker prepended to boot context in the roster editor.
 * @param {string} draftText
 * @param {string} [existing]
 * @returns {string}
 */
export function buildFrictionBootPrefill(draftText, existing = '') {
  const draft = typeof draftText === 'string' ? draftText.trim() : '';
  if (!draft) return typeof existing === 'string' ? existing : '';
  const block = [
    '--- Friction suggestion draft (directional only — verify tickets before Save) ---',
    draft,
    '--- end friction draft ---',
  ].join('\n');
  const base = typeof existing === 'string' ? existing.trim() : '';
  if (!base) return block;
  if (base.includes('--- Friction suggestion draft')) return base;
  return `${base}\n\n${block}`;
}

/**
 * Chip / summary label: cited vs matching tag hits.
 * @param {{
 *   evidenceCount?: number,
 *   evidenceTicketIds?: string[],
 *   matchingTicketCount?: number,
 *   slice?: { tagHits?: number },
 * }} suggestion
 * @returns {string}
 */
export function formatFrictionEvidenceChip(suggestion) {
  const cited =
    Number(suggestion?.evidenceCount) ||
    (Array.isArray(suggestion?.evidenceTicketIds)
      ? suggestion.evidenceTicketIds.length
      : 0);
  const matching =
    Number(suggestion?.matchingTicketCount) ||
    Number(suggestion?.slice?.tagHits) ||
    0;
  if (matching > 0) {
    return `cited ${cited} of ${matching} matching tickets`;
  }
  return `cited ${cited}`;
}

/**
 * @param {{ section?: string, frictionSuggestionId?: string, agentRole?: string }} params
 * @returns {string}
 */
export function buildRosterFrictionDeepLink(params = {}) {
  const q = new URLSearchParams();
  q.set('section', params.section || 'ai-roster');
  if (params.frictionSuggestionId) {
    q.set('frictionSuggestionId', params.frictionSuggestionId);
  }
  if (params.agentRole) {
    q.set('agentRole', params.agentRole);
  }
  return `/dashboard/workspace?${q.toString()}`;
}
